import {Sync} from '@client/sync'
import {
  CalendarStore,
  TaskType,
  TaskWithColor
} from '@components/calendar/calendar-store'
import {
  minTaskHeight,
  openTask,
  roundedTo15Min,
  roundNumberTo
} from '@components/calendar/utils'
import {Sticker} from '@extensions/sticker/sticker'
import React from 'react'
import {Hub} from 'yougile-model'
import {Time} from 'yougile-util'
import {DraggableStore} from '../draggable/draggable-store'

const getStartDateTime = (
  state: CalendarStore['state'],
  draggableState: DraggableStore['state']
) => {
  if (draggableState.draggedItem.taskType === 'all-day-with-time') {
    const initialStart = Time.toTimestamp(state.initialStartDateOnDrag)

    const hoveredStart =
      initialStart + roundNumberTo(draggableState.yTraveled, 15) * Time.minute
    const days = Math.round(
      draggableState.xTraveled / draggableState.parentWidth
    )

    return days * Time.day + hoveredStart + Time.day
  }
  const initialStart = Time.toTimestamp(state.initialStartDateOnDrag)
  const hoveredStart =
    initialStart + roundNumberTo(draggableState.yTraveled, 15) * Time.minute
  const days = Math.round(draggableState.xTraveled / draggableState.parentWidth)

  return days * Time.day + hoveredStart
}

const getDeadlineTime = (
  state: CalendarStore['state'],
  draggableState: DraggableStore['state']
) => {
  const initialDeadline = Time.toTimestamp(state.initialEndDateOnDrag)

  const hoveredStart =
    initialDeadline + roundNumberTo(draggableState.yTraveled, 15) * Time.minute
  const days = Math.round(draggableState.xTraveled / draggableState.parentWidth)

  return days * Time.day + hoveredStart
}

export const mouseDown = (
  draggableStore: DraggableStore,
  opts: {
    e: React.MouseEvent
    store: CalendarStore
    task: TaskWithColor
    type: TaskType
    allDay: boolean
    ref: React.MutableRefObject<HTMLElement>
    height: number
    allDayWidth: number
    allDayTop: number
  }
) => {
  const {e, store, task, type, allDay, ref, height, allDayWidth, allDayTop} =
    opts

  const resizable = ref.current as HTMLDivElement

  if (resizable) {
    draggableStore.clearDraggableState()
    draggableStore.setInitial(e.clientX, e.clientY)
    allDay
      ? draggableStore.setParentWidth(
          resizable.parentElement.parentElement.offsetWidth
        )
      : draggableStore.setParentWidth(resizable.parentElement.offsetWidth)
    draggableStore.setDraggedItem({
      id: task.task.id,
      changeAction: 'drag',
      taskType: type,
      height: height,
      width: allDayWidth ?? 0,
      top: allDayTop ?? 0,
      backgroundColor: task.columnColor,
      gtdStage: task.task.gtdStage,
      title: task.task.title,
      isColorDark: task.isColorDark,
      assigned: task.task.assigned,
      isSubtask: task.isSubtask
    })
    task.roundedStartDate &&
      store.setInitialDragStartDate(task.roundedStartDate)
    task.roundedDeadline && store.setInitialDragEndDate(task.roundedDeadline)
  }
}

export const mouseMove = (
  draggableState: DraggableStore['state'],
  draggableStore: DraggableStore,
  opts: {
    e: MouseEvent
    state: CalendarStore['state']
    task: TaskWithColor
    type: TaskType
    height: number
    allDayWidth: number
    allDayTop: number
    dragWithoutStartDate: boolean
  }
) => {
  const {
    state,
    e,
    task,
    type,
    height,
    allDayWidth,
    allDayTop,
    dragWithoutStartDate
  } = opts
  const event = e.target as HTMLDivElement

  if (event.dataset.day) {
    if (
      draggableState.draggedItem.taskType === 'with-start-date' ||
      draggableState.draggedItem.taskType === 'without-start-date' ||
      draggableState.draggedItem.taskType === 'all-day-with-time'
    ) {
      draggableStore.setDraggedItem({
        id: task.task.id,
        changeAction: 'drag',
        taskType: 'from-hour-to-day',
        height: minTaskHeight,
        width: draggableState.parentWidth,
        top: 0,
        backgroundColor: task.columnColor,
        gtdStage: task.task.gtdStage,
        title: task.task.title,
        isColorDark: task.isColorDark,
        assigned: task.task.assigned,
        isSubtask: task.isSubtask
      })
    }
    if (!draggableState.enabled) {
      draggableStore.setEnabled(true)
    }
    if (draggableState.enabled) {
      const end = new Date(event.dataset.day)
      draggableStore.setStartDate(end)
      draggableStore.setDeadline(end)
      draggableStore.setPlaceholderDate(end)
    }
  } else if (event.dataset.hour) {
    if (
      draggableState.draggedItem.taskType === 'all-day-single' ||
      draggableState.draggedItem.taskType === 'all-day-multiple' ||
      draggableState.draggedItem.taskType === 'all-day-with-time'
    ) {
      draggableStore.setDraggedItem({
        id: task.task.id,
        changeAction: 'drag',
        taskType: 'from-day-to-hour',
        height: height,
        width: allDayWidth ?? 0,
        top: allDayTop ?? 0,
        backgroundColor: task.columnColor,
        gtdStage: task.task.gtdStage,
        title: task.task.title,
        isColorDark: task.isColorDark,
        assigned: task.task.assigned,
        isSubtask: task.isSubtask
      })
    }
    if (!draggableState.enabled) {
      draggableStore.setEnabled(true)
    }
    if (draggableState.enabled) {
      const start = new Date(event.dataset.hour)
      const end = Time.toLocalDate(Time.toTimestamp(start) + Time.hour)

      draggableStore.setDeadline(end)
      draggableStore.setStartDate(start)
      draggableStore.setPlaceholderDate(start)
    }
  } else {
    if (
      (draggableState.draggedItem.taskType === 'from-day-to-hour' &&
        draggableState.enabled) ||
      (draggableState.draggedItem.taskType === 'from-hour-to-day' &&
        draggableState.enabled)
    ) {
      draggableStore.hoveredItemId(null)
      draggableStore.showedDrag(false)
      draggableStore.setDraggedItem({
        id: task.task.id,
        changeAction: 'drag',
        taskType: type,
        height: height,
        width: allDayWidth ?? 0,
        top: allDayTop ?? 0,
        backgroundColor: task.columnColor,
        gtdStage: task.task.gtdStage,
        title: task.task.title,
        isColorDark: task.isColorDark,
        assigned: task.task.assigned,
        isSubtask: task.isSubtask
      })
    }
    if (draggableStore.canDragBeEnabled && !draggableState.enabled) {
      draggableStore.hoveredItemId(null)
      draggableStore.showedDrag(false)
      draggableStore.setDraggedItem({
        id: task.task.id,
        changeAction: 'drag',
        taskType: type,
        height: height,
        width: allDayWidth ?? 0,
        top: allDayTop ?? 0,
        backgroundColor: task.columnColor,
        gtdStage: task.task.gtdStage,
        title: task.task.title,
        isColorDark: task.isColorDark,
        assigned: task.task.assigned,
        isSubtask: task.isSubtask
      })
      draggableStore.setEnabled(true)
    }
    if (draggableState.enabled) {
      if (dragWithoutStartDate) {
        const localTime = Time.toLocalDate(
          getDeadlineTime(state, draggableState)
        )
        draggableStore.setDeadline(localTime)
        draggableStore.setPlaceholderDate(localTime)
      } else {
        const localStartDateTime = Time.toLocalDate(
          getStartDateTime(state, draggableState)
        )
        const localDeadlineTime = Time.toLocalDate(
          getDeadlineTime(state, draggableState)
        )
        draggableStore.setDeadline(localDeadlineTime)
        draggableStore.setStartDate(localStartDateTime)
        draggableStore.setPlaceholderDate(localStartDateTime)
      }
    }
  }
}

export const mouseUp = (
  draggableState: DraggableStore['state'],
  draggableStore: DraggableStore,
  opts: {
    e: MouseEvent
    state: CalendarStore['state']
    store: CalendarStore
    task: TaskWithColor
    dragWithoutStartDate: boolean
    allDay: boolean
  }
) => {
  const {state, store, e, task, dragWithoutStartDate, allDay} = opts

  if (draggableState.enabled) {
    const event = e.target as HTMLDivElement
    let to: Date = null
    if (dragWithoutStartDate) {
      const time = Time.toTimestamp(draggableState.deadline) + 15 * Time.minute
      to = roundedTo15Min(Time.toLocalDate(time))
    } else {
      to = roundedTo15Min(draggableState.startDate)
    }
    if (event.dataset.hour) {
      to = new Date(event.dataset.hour)
    }
    const timestampTo =
      draggableState.draggedItem.taskType === 'all-day-with-time'
        ? Time.toTimestamp(to) - Time.day
        : Time.toTimestamp(to)
    const box = Sync.getCurrentSandbox()
    const taskHub = box.get(draggableState.draggedItem.id) as Hub
    if (taskHub instanceof Hub) {
      if (draggableState.draggedItem.changeAction === 'drag') {
        if (dragWithoutStartDate) {
          if (
            draggableState.draggedItem.taskType === 'from-day-to-hour' &&
            event.dataset.hour
          ) {
            Sticker.getDeadline(taskHub).current.startDate = timestampTo
            Sticker.getDeadline(taskHub).current.deadline =
              timestampTo + Time.hour
            Sticker.getDeadline(taskHub).current.withTime = true
            taskHub.commit()
          } else if (
            draggableState.draggedItem.taskType === 'from-hour-to-day' &&
            event.dataset.day
          ) {
            Sticker.getDeadline(taskHub).current.startDate = null
            Sticker.getDeadline(taskHub).current.deadline = Time.toTimestamp(
              draggableState.deadline
            )
            Sticker.getDeadline(taskHub).current.withTime = false
            taskHub.commit()
          } else {
            Sticker.getDeadline(taskHub).current.deadline = allDay
              ? Time.toTimestamp(draggableState.deadline)
              : timestampTo
            taskHub.commit()
          }
        } else {
          if (
            draggableState.draggedItem.taskType === 'from-day-to-hour' &&
            event.dataset.hour
          ) {
            Sticker.getDeadline(taskHub).current.startDate = timestampTo
            Sticker.getDeadline(taskHub).current.deadline =
              timestampTo + Time.hour
            Sticker.getDeadline(taskHub).current.withTime = true
            taskHub.commit()
          } else if (
            draggableState.draggedItem.taskType === 'from-hour-to-day' &&
            event.dataset.day
          ) {
            Sticker.getDeadline(taskHub).current.startDate = null
            Sticker.getDeadline(taskHub).current.deadline = Time.toTimestamp(
              draggableState.deadline
            )
            Sticker.getDeadline(taskHub).current.withTime = false
            taskHub.commit()
          } else {
            const beforeFrom = Time.toTimestamp(state.initialStartDateOnDrag)
            const beforeTo = Time.toTimestamp(state.initialEndDateOnDrag)
            const diff = beforeTo - beforeFrom
            Sticker.getDeadline(taskHub).current.startDate = timestampTo
            Sticker.getDeadline(taskHub).current.deadline = timestampTo + diff
            taskHub.commit()
          }
        }
        store.syncState(() => draggableStore.clearDraggableState())
      }
    }
    store.setShowAllDayWithStartDateTask(false)
  } else {
    draggableStore.clearDraggableState()
    openTask(task.task)
  }
}
